import React, { useState } from 'react';
import AskAiAnswerPanel from './AskAiAnswerPanel';
import ContentCopyGuard from './ContentCopyGuard';
import HtmlWithMermaid from './HtmlWithMermaid';

interface PostViewerProps {
  contentId: number;
  contentHtml: string;
  onComplete: (contentId: number) => void;
  isCompleted: boolean;
  title?: string;
}

const PostViewer: React.FC<PostViewerProps> = ({ contentId, contentHtml, onComplete, isCompleted, title }) => {
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [askAiSelection, setAskAiSelection] = useState('');

  const openAskAi = (selectedText: string) => {
    setAskAiSelection(selectedText);
    setAskAiOpen(true);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-m2-outline p-3 md:p-4 w-full h-full flex flex-col min-h-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-m2-outline pb-2 mb-3 shrink-0">
        <h2 className="text-base sm:text-lg md:text-xl font-bold text-m2-on-surface line-clamp-2 sm:line-clamp-1 min-w-0">
          {title || 'Bài học'}
        </h2>
        <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => openAskAi('')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-m2-primary/30 bg-m2-primary/10 px-2.5 py-1.5 text-xs font-semibold text-m2-primary hover:bg-m2-primary/15"
            title="Hỏi AI về bài viết (hoặc chuột phải trên nội dung)"
          >
            <i className="fas fa-robot"></i>
            Ask AI
          </button>
          <button
            onClick={() => onComplete(contentId)}
            disabled={isCompleted}
            className={`shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-bold shadow-sm transition-all flex items-center gap-1.5 ${
              isCompleted
                ? 'bg-emerald-500 text-white cursor-not-allowed border border-emerald-400'
                : 'bg-m2-primary hover:bg-eh-forest-shadow text-white'
            }`}
          >
            {isCompleted ? (
              <>
                <i className="fas fa-check-circle"></i>
                <span>Đã hoàn thành</span>
              </>
            ) : (
              <>
                <i className="fas fa-check"></i>
                <span>Hoàn thành</span>
              </>
            )}
          </button>
        </div>
      </div>

      <ContentCopyGuard className="flex-grow overflow-y-auto custom-scrollbar min-h-0 pr-1" onAskAi={openAskAi}>
        <HtmlWithMermaid
          html={contentHtml || ''}
          className="post-viewer-html html-media-center prose max-w-none text-m2-on-surface
            prose-headings:text-m2-on-surface
            prose-p:text-m2-on-surface
            prose-li:text-m2-on-surface
            prose-strong:text-m2-on-surface
            prose-a:font-medium prose-a:text-m2-primary prose-a:underline
            prose-blockquote:text-m2-on-surface-variant
            prose-code:text-m2-primary
            prose-pre:bg-slate-50 prose-pre:text-slate-800 prose-pre:border prose-pre:border-slate-200"
        />
      </ContentCopyGuard>

      {askAiOpen && (
        <AskAiAnswerPanel
          open={askAiOpen}
          onClose={() => setAskAiOpen(false)}
          mode="content"
          title={title || 'Bài học'}
          contentHtml={contentHtml || ''}
          selectedText={askAiSelection}
        />
      )}
    </div>
  );
};

export default PostViewer;
